import { Avatar } from 'components/Avatar';
import { Button } from 'components/Button';
import { CATEGORY3FILTERS, numberOfSlots } from 'configs/active/constants';
import { ILocation, ISchedule, ISpeaker, ITeam, ITeamBooking } from 'configs/interfaces';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Dispatch, IRootState, useRematchDispatch } from 'rematch/store';
import useReactRouter from 'use-react-router';
import _ from 'lodash';
import { Card } from 'components/Container';
import { isEmptyString } from 'helper';
import Table from '../assets/table.png';
import moment from 'moment';
import { RichtextRenderer } from 'components/Richtext';
import { EIcon } from 'configs/constants';
import { Icon } from 'components/Icon';
import { Redirect } from 'react-router-dom';

export const DetailsScreen = () => {
	const { history, location } = useReactRouter();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [team, setTeam] = useState<ITeam | undefined>(undefined);
	const [schedule, setSchedule] = useState<ISchedule | undefined>(undefined);
	const [speaker, setSpeaker] = useState<ISpeaker | undefined>(undefined);
	const [teamLocation, setTeamLocation] = useState<ILocation | undefined>(undefined);
	const [teamBooking, setTeamBooking] = useState<ITeamBooking | undefined>(undefined);
	const [isMoreDetailsCollapsed, setIsMoreDetailsCollapsed] = useState<boolean>(false);
	const [remainingSeats, setRemainingSeats] = useState<number>(8);

	const teams = useSelector((store: IRootState) => store.data.teams);
	const myBookings = useSelector((store: IRootState) => store.booking.myBookings);
	const teamBookings = useSelector((store: IRootState) => store.booking.teamBookings);

	const bookSlot = useRematchDispatch((dispatch: Dispatch) => dispatch.booking.bookSlot);
	const cancelSlot = useRematchDispatch((dispatch: Dispatch) => dispatch.booking.cancelSlot);

	useEffect(() => {
		let _schedule: typeof schedule = undefined;
		let _team: typeof team = undefined;
		let _location: typeof teamLocation = undefined;
		let _speaker: typeof speaker = undefined;

		if (teams && location.state) {
			const { teamId } = location.state as any;

			_team = teams.find((e) => e.id === teamId);
			if (_team) {
				if (_team.schedules) {
					_schedule = _team.schedules.find((e) => !e.isDeleted);
				}
				if (_team.locations) {
					_location = _team.locations.find((e) => !e.isDeleted);
				}
				if (_team.speakers) {
					_speaker = _team.speakers.find((e) => !e.isDeleted);
				}
			}
		}

		setSchedule(_schedule);
		setTeam(_team);
		setTeamLocation(_location);
		setSpeaker(_speaker);
	}, [teams]);

	useEffect(() => {
		let _teamBooking: typeof teamBooking = undefined;

		if (team && location.state) {
			const { slotId } = location.state as any;

			_teamBooking = teamBookings.find((e) => e.slotId === slotId && e.teamId === team.id);
			if (team.maxUsers) {
				let _remaining = team.maxUsers;

				const teamBooking = teamBookings.find((e) => e.slotId === slotId && e.teamId === team.id);
				if (teamBooking) {
					_remaining = Math.max(0, _remaining - teamBooking.users.length);
				}
				setRemainingSeats(_remaining);
			}
		}

		setTeamBooking(_teamBooking);
	}, [team, teamBookings]);

	const _bookSlot = async () => {
		const { slotId, teamId } = location.state as any;

		setIsLoading(true);
		await bookSlot({ slotId, teamId });
		setIsLoading(false);
	};

	const _cancelSlot = async () => {
		const { slotId } = location.state as any;

		setIsLoading(true);
		await cancelSlot({ slotId });
		setIsLoading(false);
	};

	const _iHaveBookedThisTeam = () => {
		const { slotId, teamId } = location.state as any;

		return myBookings?.slots.find((e) => e.slotId === slotId && e.teamId === teamId) !== undefined;
	};

	const _renderJobtitleAndCompany = () => {
		let str = '';

		if (speaker?.jobTitle && speaker?.company) {
			str = `${speaker.jobTitle} | ${speaker.company}`;
		} else if (speaker?.jobTitle) {
			str = speaker.jobTitle;
		} else if (speaker?.company) {
			str = speaker.company;
		}

		return <div>{str}</div>;
	};

	const _renderUser = (idx: number) => {
		let fullName;
		if (teamBooking) {
			if (teamBooking.users[idx]) {
				fullName = `${teamBooking.users[idx].firstName} ${teamBooking.users[idx].lastName}`;
			}
		}

		if (team?.maxUsers && idx + 1 <= team?.maxUsers) {
			return (
				<>
					<Avatar fullName={fullName} showNameOnHover user={teamBooking?.users[idx]} size="xs" containerCss="flex md:hidden" />
					<Avatar
						fullName={fullName}
						showNameOnHover
						user={teamBooking?.users[idx]}
						size="sm"
						containerCss="hidden md:flex xl:hidden"
					/>
					<Avatar fullName={fullName} showNameOnHover user={teamBooking?.users[idx]} size="md" containerCss="hidden xl:flex" />
				</>
			);
		}

		return null;
	};

	const _renderTableCaptain = () => {
		let fullName;

		const splitted = team?.teamCategory2 ? team.teamCategory2.split(',') : [];

		if (splitted.includes('topictable')) {
			if (team?.maxUsers && 8 <= team?.maxUsers) {
				if (teamBooking) {
					if (teamBooking.users[7]) {
						fullName = `${teamBooking.users[7].firstName} ${teamBooking.users[7].lastName}`;
					}
				}

				return (
					<>
						<Avatar fullName={fullName} showNameOnHover user={teamBooking?.users[7]} size="xs" containerCss="flex md:hidden" />
						<Avatar
							fullName={fullName}
							showNameOnHover
							user={teamBooking?.users[7]}
							size="sm"
							containerCss="hidden md:flex xl:hidden"
						/>
						<Avatar fullName={fullName} showNameOnHover user={teamBooking?.users[7]} size="md" containerCss="hidden xl:flex" />
					</>
				);
			}
		}

		fullName = speaker?.title ?? team?.title;

		return (
			<>
				<Avatar
					fullName={fullName}
					avatar={speaker?.image?.url ?? team?.image?.url}
					showNameOnHover
					size="xs"
					containerCss="flex md:hidden"
				/>
				<Avatar
					fullName={fullName}
					avatar={speaker?.image?.url ?? team?.image?.url}
					showNameOnHover
					size="sm"
					containerCss="hidden md:flex xl:hidden"
				/>
				<Avatar
					fullName={fullName}
					avatar={speaker?.image?.url ?? team?.image?.url}
					showNameOnHover
					size="md"
					containerCss="hidden xl:flex"
				/>
			</>
		);
	};

	if (location.state) {
		const { slotId, teamId } = location.state as any;

		if (slotId && teamId) {
			return (
				<Card hatColor="text-success-500">
					<div className="flex-1 flex flex-col md:flex-row">
						<div className="flex flex-col w-full md:w-1/2 px-4 md:px-12">
							<div className="flex flex-col w-full items-center mx-auto">
								<Avatar
									containerCss="my-auto"
									size="xxl"
									avatar={speaker?.image?.url ?? team?.image?.url}
									fullName={speaker?.title ?? team?.title}
								/>
								<div className="flex items-center mt-2">
									<div className="font-bold text-primary-500">{speaker?.title ?? team?.title}</div>
									{speaker?.linkedInProfileUrl && (
										<button onClick={() => window.open(speaker.linkedInProfileUrl, '_blank')} className="ml-2">
											<Icon name={EIcon.LinkedIn} className="text-xl text-primary-500" />
										</button>
									)}
								</div>
								{_renderJobtitleAndCompany()}
								<div className="flex w-full justify-between mt-2">
									<div className="flex flex-col">
										<div className="text-primary-500 font-bold border-b border-lightgray">Cluster</div>
										{team?.teamCategory3?.split(',').map((e) => {
											const option = CATEGORY3FILTERS.find((e2) => e2.key === e);
											if (option) {
												return (
													<div
														key={`${team.id}_tag_${e}`}
														className="bg-background px-2 py-1 mt-2 text-sm text-center"
													>
														{option.label}
													</div>
												);
											}
											return null;
										})}
									</div>
									<div>
										<div className="flex flex-col">
											<div className="text-primary-500 font-bold border-b border-lightgray">
												{moment(schedule?.startDate).format('DD MMMM')}
											</div>
											<div className="px-2 py-1 rounded-lg mt-2 bg-primary-500 text-white text-sm">{`${moment(
												schedule?.startDate,
											).format('HH:mm')} - ${moment(schedule?.endDate).format('HH:mm')}`}</div>
										</div>

										<div className="flex flex-col text-center">
											<div className="text-primary-500 font-bold border-b border-lightgray">Row</div>
											<div className="bg-primary-500 text-white px-2 py-1 rounded-lg mt-2 text-center">
												{teamLocation?.title ?? 'TBD'}
											</div>
										</div>
										<div className="flex flex-col text-center">
											<div className="text-primary-500 font-bold border-b border-lightgray">Table</div>
											<div className="bg-primary-500 text-white px-2 py-1 rounded-lg mt-2 text-center">
												{teamLocation?.subtitle ?? 'TBD'}
											</div>
										</div>
									</div>
								</div>

								{!isEmptyString(speaker?.bio) && (
									<div className="flex flex-col w-full mt-4">
										<div className="w-full flex items-center justify-between">
											<div className="text-primary-500 font-bold border-b border-lightgray">More details</div>
											<button onClick={() => setIsMoreDetailsCollapsed(!isMoreDetailsCollapsed)}>
												<Icon
													name={isMoreDetailsCollapsed ? EIcon.ArrowDown : EIcon.ArrowUp}
													className="text-primary-500 text-xl"
												/>
											</button>
										</div>
										{!isMoreDetailsCollapsed && <RichtextRenderer richtext={speaker?.bio} />}
									</div>
								)}

								<div className="flex items-center justify-between mt-12">
									{!_iHaveBookedThisTeam() && (
										<Button
											title="< all tables"
											type="primary"
											buttonCss="mb-6 mr-6"
											onClick={() =>
												history.push({
													pathname: 'overview',
													state: {
														slotId,
													},
												})
											}
											isDisabled={isLoading}
										/>
									)}
									<Button
										title={numberOfSlots > 1 ? '< my bookings' : '< my booking'}
										type="primary"
										buttonCss="mb-6"
										isOutline
										onClick={() => history.push('/')}
										isDisabled={isLoading}
									/>
								</div>
							</div>
						</div>
						<div className="flex flex-col w-full md:w-1/2 md:px-4 xl:px-8">
							<div
								className={`${
									remainingSeats > 0 ? 'text-primary-500' : 'text-lightgray'
								} font-bold border-b border-lightgray`}
							>{`${remainingSeats} places remaining`}</div>
							<div className="flex items-center">
								<div className="h-full flex flex-col justify-center xl:justify-between px-2 xl:px-4 xl:py-48">
									<div className="mb-4">{_renderTableCaptain()}</div>
									{_renderUser(6)}
								</div>
								<div className="flex-1">
									<div className=" w-full flex items-center justify-between py-2 xl:py-6 px-7 md:px-12 xl:px-20">
										{_renderUser(0)}
										{_renderUser(1)}
									</div>
									<div>
										<img src={Table} className="w-full object-contain" />
									</div>
									<div className=" w-full flex items-center justify-between py-2 xl:py-6 px-7 md:px-12 xl:px-20">
										{_renderUser(5)}
										{_renderUser(4)}
									</div>
									<div className="text-center text-sm mt-2">{`${teamBooking?.users.length ?? 0}/${
										team?.maxUsers
									} places taken`}</div>
								</div>
								<div className="h-full flex flex-col justify-center xl:justify-between px-2 xl:py-48">
									<div className="mb-4">{_renderUser(2)}</div>
									{_renderUser(3)}
								</div>
							</div>
						</div>
					</div>
					<div className="w-full md:w-1/2 mx-auto flex">
						<Button
							title={_iHaveBookedThisTeam() ? 'Leave table' : remainingSeats === 0 ? 'Fully booked' : 'Join table'}
							type={_iHaveBookedThisTeam() || remainingSeats === 0 ? 'danger' : 'success'}
							buttonCss="w-full my-6"
							isDisabled={!_iHaveBookedThisTeam() && remainingSeats === 0}
							onClick={() => {
								if (_iHaveBookedThisTeam()) {
									_cancelSlot();
								} else {
									_bookSlot();
								}
							}}
							isLoading={isLoading}
						/>
					</div>
				</Card>
			);
		}
	}

	return <Redirect to="/" />;
};
